import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  PAID_PLAN_FEATURE_KEYS,
  PaidPlanOfferingCard,
  toLandingBillingInterval,
} from '@/components/billing/paid-plan-offering';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/utils/cn';
import { formatMoney } from '@/utils/format';

import type { SubscriptionsSummary } from './subscriptions-types';

const quoteCurrentPlanTotal = (summary: SubscriptionsSummary): string | null => {
  const { subscription, checkout } = summary;
  if (subscription.plan !== 'essential' && subscription.plan !== 'professional') return null;
  const planQuote = checkout?.plans?.find((row) => row.plan === subscription.plan);
  if (!planQuote) return null;
  const interval = subscription.billingInterval ?? 'annual';
  const amountMinor =
    interval === 'monthly' ? planQuote.monthlyAmountMinor : planQuote.annualAmountMinor;
  return formatMoney(amountMinor, planQuote.currency);
};

const EnterprisePlanDetails = () => {
  const { t } = useTranslation('landing');
  const { t: tBilling } = useTranslation('billing');

  return (
    <article className="relative flex flex-col rounded-2xl border border-brand/40 bg-surface p-5 ring-1 ring-brand/10 sm:p-6">
      <div className="space-y-2">
        <h3 className="text-card-title font-semibold text-fg">{t('pricing.plans.enterprise.name')}</h3>
        <p className="text-pretty text-body-sm leading-relaxed text-fg-secondary">
          {t('pricing.plans.enterprise.body')}
        </p>
      </div>

      <div className="mt-5 space-y-1 border-b border-border pb-5">
        <p className="text-section-title font-semibold text-brand">
          {t('pricing.plans.enterprise.price.monthly')}
        </p>
        <p className="text-body-sm text-fg-secondary">{t('pricing.plans.enterprise.period.monthly')}</p>
      </div>

      <ul className="mt-5 grid gap-2.5">
        {PAID_PLAN_FEATURE_KEYS.map((featureKey) => (
          <li key={featureKey} className="flex gap-3 text-body-sm leading-snug text-fg-secondary">
            <Check className="mt-0.5 size-4 shrink-0 text-brand" aria-hidden />
            <span>{t(`pricing.plans.enterprise.features.${featureKey}`)}</span>
          </li>
        ))}
      </ul>

      <p className="mt-6 text-body-sm text-fg-secondary">{tBilling('plans.noActionDescription')}</p>
    </article>
  );
};

const FreePlanDetails = ({ summary }: { summary: SubscriptionsSummary }) => {
  const { t } = useTranslation('billing');
  const plan = summary.subscription.plan;

  const paragraphs: Array<{ key: string; className?: string }> = [];

  if (plan === 'community') {
    paragraphs.push({ key: 'community.activeDescription' });
  }
  if (plan === 'starter') {
    paragraphs.push({ key: 'starter.activeDescription' });
  }
  if (summary.community.qualifies && plan === 'trial') {
    paragraphs.push({
      key: 'community.eligibleDescription',
    });
  }
  if (summary.starter?.qualifies && plan === 'trial' && !summary.community.qualifies) {
    paragraphs.push({ key: 'starter.eligibleDescription' });
  }
  if (summary.community.requiresUpgrade) {
    paragraphs.push({ key: 'community.requiresUpgrade', className: 'text-warning' });
  }
  if (summary.community.requiresPaidUpgrade) {
    paragraphs.push({ key: 'community.requiresPaidUpgrade', className: 'text-warning' });
  }
  if (summary.starter?.requiresUpgrade) {
    paragraphs.push({ key: 'starter.requiresUpgrade', className: 'text-warning' });
  }

  if (plan === 'trial' && paragraphs.length === 0) {
    paragraphs.push({ key: 'overview.includedSummaryNone' });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t(`plans.${plan}`)}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-body-sm text-fg-secondary">
        {paragraphs.map(({ key, className }) => (
          <p key={key} className={cn(className)}>
            {key === 'community.eligibleDescription'
              ? t(key, {
                  count: summary.community.memberCount,
                  limit: summary.community.limit,
                })
              : key === 'starter.eligibleDescription'
                ? t(key, {
                    count: summary.starter?.memberCount ?? 0,
                    limit: summary.starter?.limit ?? 0,
                  })
                : t(key)}
          </p>
        ))}
      </CardContent>
    </Card>
  );
};

export const CurrentPlanDetails = ({ summary }: { summary: SubscriptionsSummary }) => {
  const { t } = useTranslation('billing');
  const plan = summary.subscription.plan;
  const billingInterval = summary.subscription.billingInterval ?? 'annual';
  const totalPrice = quoteCurrentPlanTotal(summary);
  const billingIntervalLabel =
    billingInterval === 'monthly' ? t('checkout.perMonth') : t('checkout.perYear');

  if (plan === 'essential' || plan === 'professional') {
    return (
      <PaidPlanOfferingCard
        plan={plan}
        interval={toLandingBillingInterval(billingInterval)}
        isCurrent
        featured={plan === 'professional'}
        currentPlanBadge={t('plans.currentPlan')}
        footer={
          totalPrice ? (
            <p className="text-center text-body-sm text-fg-secondary">
              {t('overview.totalBilled', { amount: totalPrice, interval: billingIntervalLabel })}
            </p>
          ) : null
        }
      />
    );
  }

  if (plan === 'enterprise') {
    return <EnterprisePlanDetails />;
  }

  return <FreePlanDetails summary={summary} />;
};
